/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUniversity, faMapMarkerAlt, faUser, faPhone,
  faEnvelope, faLink, faUpload, faCheckCircle, 
  faInfoCircle, faCheck, faShieldAlt, faSave, faGraduationCap,
  faBuilding, faUsers, faBriefcase, faGlobe, faFileAlt,
  faChartLine, faCertificate, faCalendar, faTimes, faEdit, faImage, faFilePdf
} from '@fortawesome/free-solid-svg-icons';
import { faLinkedin, faFacebook, faTwitter } from '@fortawesome/free-brands-svg-icons';
import { buildApiUrl } from '../config/api';
import { useAutoSave } from '../hooks/useAutoSave';
import axios from 'axios';
import PhoneInput from '../components/PhoneInput';
import jsPDF from 'jspdf';
import '../styles/RecruiterRegistrationForm.css';
import 'leaflet/dist/leaflet.css';
import logoImage from '../assets/FINAL LOGO AK.png';

// Import Leaflet directly
import L from 'leaflet';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const KENYAN_COUNTIES = [
  "Baringo", "Bomet", "Bungoma", "Busia", "Elgeyo-Marakwet", "Embu", "Garissa", "Homa Bay",
  "Isiolo", "Kajiado", "Kakamega", "Kericho", "Kiambu", "Kilifi", "Kirinyaga", "Kisii",
  "Kisumu", "Kitui", "Kwale", "Laikipia", "Lamu", "Machakos", "Makueni", "Mandera",
  "Marsabit", "Meru", "Migori", "Mombasa", "Murang'a", "Nairobi", "Nakuru", "Nandi",
  "Narok", "Nyamira", "Nyandarua", "Nyeri", "Samburu", "Siaya", "Taita-Taveta", "Tana River",
  "Tharaka-Nithi", "Trans Nzoia", "Turkana", "Uasin Gishu", "Vihiga", "Wajir", "West Pokot"
];

const INSTITUTION_TYPES = [
  "Public University",
  "Private University",
  "Technical Institute",
  "College",
  "Polytechnic",
  "Teacher Training College",
  "Medical Training College"
];

const AREAS_OF_SPECIALIZATION = [
  "Engineering", "Business Administration", "Information Technology", "Medicine & Health Sciences",
  "Education", "Arts & Humanities", "Sciences", "Agriculture", "Law", "Architecture",
  "Nursing", "Hospitality & Tourism", "Communication & Media", "Social Sciences",
  "Mathematics & Statistics", "Environmental Sciences", "Economics", "Psychology"
];

const UniversityRegistrationForm = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const mapSearchInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [useMapAddress, setUseMapAddress] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // Auto-save configuration
  const AUTOSAVE_KEY = `university_registration_${user?.userId || 'temp'}`;

  // Use auto-save hook
  const {
    formData,
    setFormData,
    isSaving,
    saveStatus,
    lastSaveTime,
    lastBackendSaveTime,
    forceSave,
    hasRecentSave,
    clearSavedData
  } = useAutoSave({
    // Institution Information
    universityName: '',
    institutionType: '',
    registrationNumber: '',
    cueRegistrationNumber: '',
    yearEstablished: '',
    institutionLogo: '',
    showLogoOnWebsite: true,
    
    // Contact Information
    officialEmail: '',
    alternativeEmail: '',
    phoneNumber: '',
    whatsappNumber: '',
    physicalAddress: '',
    city: '',
    county: '',
    postalCode: '',
    latitude: '',
    longitude: '',
    
    // Primary Contact Person
    contactPersonName: '',
    contactPersonTitle: '',
    contactPersonDepartment: '',
    contactPersonPhone: '',
    contactPersonEmail: '',
    
    // Institution Details
    websiteUrl: '',
    totalStudentEnrollment: '',
    numberOfPrograms: '',
    institutionDescription: '',
    accreditationBodies: '',
    
    // Career Services Information
    careerCenterEmail: '',
    careerOfficePhone: '',
    careerServicesContactPerson: '',
    
    // Optional Information
    linkedinProfile: '',
    facebookPage: '',
    twitterHandle: '',
    
    // Agreements
    agreeTerms: false,
    agreeDataPrivacy: false,
    verifyAuthorization: false
  },
  AUTOSAVE_KEY,
  1000,
  null, // Backend callback will be handled via manual save functions
  true // Enable periodic auto-save to localStorage
);

  const [areasOfSpecialization, setAreasOfSpecialization] = useState([]);

  // Persist array-based fields across refreshes
  const ARRAYS_AUTOSAVE_KEY = useMemo(() => `${AUTOSAVE_KEY}_arrays`, [AUTOSAVE_KEY]);

  // Hydrate arrays on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(ARRAYS_AUTOSAVE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed?.areasOfSpecialization)) setAreasOfSpecialization(parsed.areasOfSpecialization);
      }
    } catch (_) { /* ignore */ }
  }, [ARRAYS_AUTOSAVE_KEY]);

  // Save arrays whenever they change
  useEffect(() => {
    const payload = { areasOfSpecialization };
    try {
      localStorage.setItem(ARRAYS_AUTOSAVE_KEY, JSON.stringify(payload));
    } catch (_) { /* ignore quota errors */ }
  }, [areasOfSpecialization, ARRAYS_AUTOSAVE_KEY]);

  // Update logo preview when formData.institutionLogo changes
  useEffect(() => {
    if (formData.institutionLogo && formData.institutionLogo !== logoPreview) {
      setLogoPreview(formData.institutionLogo);
    }
  }, [formData.institutionLogo]);

  // Define setMarker function before useEffect that uses it
  const setMarker = useCallback((lat, lng, forceUpdateAddress = false) => {
    const map = mapInstanceRef.current;
    if (!map) return;

    if (markerRef.current) {
      map.removeLayer(markerRef.current);
    }
    
    const marker = L.marker([lat, lng], {
      draggable: true
    }).addTo(map);

    markerRef.current = marker;

    setFormData(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng
    }));

    marker.on('dragend', function(e) {
      const position = marker.getLatLng();
      setFormData(prev => ({
        ...prev,
        latitude: position.lat,
        longitude: position.lng
      }));
      
      // Only reverse geocode if user wants to use map address
      if (useMapAddress) {
        fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${position.lat}&lon=${position.lng}`)
          .then(response => response.json())
          .then(data => {
            if (data.display_name) {
              setFormData(prev => ({
                ...prev,
                physicalAddress: data.display_name
              }));
            }
          })
          .catch(error => {
            console.log('Reverse geocoding failed:', error);
          });
      }
    });

    // Only reverse geocode if user wants to use map address or it's forced (initial load)
    if (useMapAddress || forceUpdateAddress) {
      fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`)
        .then(response => response.json())
        .then(data => {
          if (data.display_name) {
            setFormData(prev => ({
              ...prev,
              physicalAddress: data.display_name
            }));
          }
        })
        .catch(error => {
          console.log('Reverse geocoding failed:', error);
        });
    }
  }, [useMapAddress, setFormData]);

  // Initialize map
  useEffect(() => {
    const initMap = () => {
      if (mapRef.current && !mapInstanceRef.current) {
        // Default center (Nairobi, Kenya)
        const map = L.map(mapRef.current).setView([-1.286389, 36.817223], 12);
        
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors',
          maxZoom: 19
        }).addTo(map);

        mapInstanceRef.current = map;

        // Add click event to map
        map.on('click', function(e) {
          setMarker(e.latlng.lat, e.latlng.lng, false);
        });

        // Try to get user's current location
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(function(position) {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            map.setView([lat, lng], 13);
            // Don't force update address on initial geolocation
            setMarker(lat, lng, false);
          });
        }

        setMapLoaded(true);
      }
    };

    const timer = setTimeout(initMap, 100);
    return () => {
      clearTimeout(timer);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [setMarker]);

  // Search location function
  const searchLocation = useCallback((query) => {
    if (!query || !query.trim()) {
      alert('Please enter a location to search');
      return;
    }

    setIsSearching(true);
    fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`)
      .then(response => response.json())
      .then(data => {
        setIsSearching(false);
        if (data && data.length > 0) {
          const lat = parseFloat(data[0].lat);
          const lng = parseFloat(data[0].lon);
          const map = mapInstanceRef.current;
          
          if (map) {
            map.setView([lat, lng], 15);
            setMarker(lat, lng, false);
          }
        } else {
          alert('Location not found. Please try a different search term.');
        }
      })
      .catch(error => {
        setIsSearching(false);
        console.log('Search error:', error);
        alert('Error searching location. Please try again.');
      });
  }, [setMarker]);

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (mapSearchInputRef.current) {
        searchLocation(mapSearchInputRef.current.value);
      }
    }
  };

  const updateProgress = useCallback(() => {
    const requiredFields = [
      formData.universityName, formData.institutionType, formData.registrationNumber,
      formData.officialEmail, formData.phoneNumber, formData.physicalAddress,
      formData.city, formData.county, formData.contactPersonName,
      formData.contactPersonTitle, formData.contactPersonPhone, formData.contactPersonEmail
    ];

    const filledFields = requiredFields.filter(field => {
      return field && field.toString().trim() !== '';
    }).length;

    // Count array fields
    let arrayFieldsCount = 0;
    if (areasOfSpecialization.length > 0) arrayFieldsCount++;

    // Count terms checkboxes
    let termsCount = 0;
    if (formData.agreeTerms) termsCount++;
    if (formData.agreeDataPrivacy) termsCount++;
    if (formData.verifyAuthorization) termsCount++;

    // Total: 12 required fields + 1 array + 3 terms = 16 total
    const totalRequired = 16;
    const totalFilled = filledFields + arrayFieldsCount + termsCount;

    const progressPercent = Math.min((totalFilled / totalRequired) * 100, 100);
    setProgress(progressPercent);
  }, [formData, areasOfSpecialization]);

  useEffect(() => {
    updateProgress();
  }, [updateProgress]);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    updateProgress();
  }, [updateProgress, setFormData]);

  const handleSpecializationChange = (specialization) => {
    setAreasOfSpecialization(prev => {
      const updated = prev.includes(specialization)
        ? prev.filter(s => s !== specialization)
        : [...prev, specialization];
      updateProgress();
      return updated;
    });
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      if (!file.type.match(/^image\/(png|jpg|jpeg)$/i)) {
        alert('Only PNG and JPG images are allowed');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const logoData = e.target.result;
        setLogoPreview(logoData);
        setFormData(prev => ({
          ...prev,
          institutionLogo: logoData
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Save to backend function
  const saveToBackend = async (showSuccessMessage = false, redirectAfterSave = false) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('⚠️  No token found, skipping backend save');
        return false;
      }

      const submitData = {
        ...formData,
        areasOfSpecialization,
        profileCompleted: false,
        isDraft: true,
        draftSavedAt: new Date().toISOString()
      };

      const response = await fetch(buildApiUrl('/api/university/complete-registration'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        if (showSuccessMessage) {
          setSubmitError('✓ Form saved successfully!');
          setTimeout(() => setSubmitError(''), 3000);
        }
        if (redirectAfterSave) {
          setTimeout(() => {
            navigate('/university-dashboard');
          }, 1000);
        }
        return true;
      } else {
        const errorData = await response.json();
        if (showSuccessMessage) {
          setSubmitError(errorData.message || 'Failed to save. Please try again.');
        }
        return false;
      }
    } catch (error) {
      console.error('Backend save error:', error);
      if (showSuccessMessage) {
        setSubmitError('Network error. Your data is saved locally.');
      }
      return false;
    }
  };

  // Manual save handler
  const handleManualSave = async () => {
    setIsLoading(true);
    setSubmitError('');
    try {
      forceSave();
      await saveToBackend(true, false);
    } finally {
      setIsLoading(false);
    }
  };

  // Save as draft and redirect
  const handleSaveAsDraft = async () => {
    setIsLoading(true);
    setSubmitError('');
    try {
      forceSave();
      await saveToBackend(true, true);
    } catch (error) {
      console.error('Save draft error:', error);
      setSubmitError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    let yPosition = 20;
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - (margin * 2);
    const lineHeight = 7;

    // Helper function to add text with word wrap
    const addText = (text, fontSize = 10, isBold = false, color = [0, 0, 0]) => {
      doc.setFontSize(fontSize);
      doc.setTextColor(color[0], color[1], color[2]);
      if (isBold) {
        doc.setFont(undefined, 'bold');
      } else {
        doc.setFont(undefined, 'normal');
      }
      
      const lines = doc.splitTextToSize(text || 'N/A', maxWidth);
      if (yPosition + (lines.length * lineHeight) > doc.internal.pageSize.getHeight() - 20) {
        doc.addPage();
        yPosition = 20;
      }
      doc.text(lines, margin, yPosition);
      yPosition += lines.length * lineHeight + 3;
    };

    // Helper function to add section header
    const addSectionHeader = (title) => {
      if (yPosition > doc.internal.pageSize.getHeight() - 30) {
        doc.addPage();
        yPosition = 20;
      }
      yPosition += 5;
      addText(title, 14, true, [13, 148, 136]);
      yPosition += 2;
    };

    // Header
    doc.setFillColor(13, 148, 136);
    doc.rect(0, 0, pageWidth, 30, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text('University Registration Form', pageWidth / 2, 20, { align: 'center' });
    doc.setFontSize(10);
    doc.text('AksharJobs - Educational Institution Registration', pageWidth / 2, 26, { align: 'center' });
    
    yPosition = 40;
    doc.setTextColor(0, 0, 0);

    // Institution Information
    addSectionHeader('Institution Information');
    addText(`University/Institution Name: ${formData.universityName || 'N/A'}`, 11, true);
    addText(`Institution Type: ${formData.institutionType || 'N/A'}`);
    addText(`Registration Number: ${formData.registrationNumber || 'N/A'}`);
    addText(`CUE Registration Number: ${formData.cueRegistrationNumber || 'N/A'}`);
    addText(`Year Established: ${formData.yearEstablished || 'N/A'}`);

    // Contact Information
    addSectionHeader('Contact Information');
    addText(`Official Email: ${formData.officialEmail || 'N/A'}`);
    addText(`Alternative Email: ${formData.alternativeEmail || 'N/A'}`);
    addText(`Phone Number: ${formData.phoneNumber || 'N/A'}`);
    addText(`WhatsApp Number: ${formData.whatsappNumber || 'N/A'}`);
    addText(`Physical Address: ${formData.physicalAddress || 'N/A'}`);
    addText(`City: ${formData.city || 'N/A'}`);
    addText(`County: ${formData.county || 'N/A'}`);
    addText(`Postal Code: ${formData.postalCode || 'N/A'}`);
    if (formData.latitude && formData.longitude) {
      addText(`Coordinates: Lat ${parseFloat(formData.latitude).toFixed(6)}, Lng ${parseFloat(formData.longitude).toFixed(6)}`);
    }

    // Primary Contact Person
    addSectionHeader('Primary Contact Person (Placement Officer)');
    addText(`Name: ${formData.contactPersonName || 'N/A'}`, 11, true);
    addText(`Title/Position: ${formData.contactPersonTitle || 'N/A'}`);
    addText(`Department: ${formData.contactPersonDepartment || 'N/A'}`);
    addText(`Phone: ${formData.contactPersonPhone || 'N/A'}`);
    addText(`Email: ${formData.contactPersonEmail || 'N/A'}`);

    // Institution Details
    addSectionHeader('Institution Details');
    addText(`Website URL: ${formData.websiteUrl || 'N/A'}`);
    addText(`Total Student Enrollment: ${formData.totalStudentEnrollment || 'N/A'}`);
    addText(`Number of Programs: ${formData.numberOfPrograms || 'N/A'}`);
    addText(`Accreditation Bodies: ${formData.accreditationBodies || 'N/A'}`);
    if (formData.institutionDescription) {
      addText(`Description: ${formData.institutionDescription}`);
    }

    // Career Services
    addSectionHeader('Career Services/Placement Office');
    addText(`Career Center Email: ${formData.careerCenterEmail || 'N/A'}`);
    addText(`Career Office Phone: ${formData.careerOfficePhone || 'N/A'}`);
    addText(`Contact Person: ${formData.careerServicesContactPerson || 'N/A'}`);

    // Areas of Specialization
    if (areasOfSpecialization.length > 0) {
      addSectionHeader('Areas of Specialization');
      addText(areasOfSpecialization.join(', '));
    }

    // Social Media
    addSectionHeader('Social Media & Additional Information');
    addText(`LinkedIn: ${formData.linkedinProfile || 'N/A'}`);
    addText(`Facebook: ${formData.facebookPage || 'N/A'}`);
    addText(`Twitter/X: ${formData.twitterHandle || 'N/A'}`);

    // Terms & Agreements
    addSectionHeader('Terms & Agreements');
    addText(`Agreed to Terms: ${formData.agreeTerms ? 'Yes' : 'No'}`);
    addText(`Agreed to Data Privacy: ${formData.agreeDataPrivacy ? 'Yes' : 'No'}`);
    addText(`Verified Authorization: ${formData.verifyAuthorization ? 'Yes' : 'No'}`);

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Page ${i} of ${pageCount} | Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    // Save the PDF
    const fileName = formData.universityName 
      ? `${formData.universityName.replace(/[^a-z0-9]/gi, '_')}_Registration_Form.pdf`
      : 'University_Registration_Form.pdf';
    doc.save(fileName);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmitError('');

    // Validation
    if (!formData.agreeTerms || !formData.agreeDataPrivacy || !formData.verifyAuthorization) {
      setSubmitError('Please accept all required terms and conditions');
      setIsLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const submitData = new FormData();

      // Add all form fields
      Object.keys(formData).forEach(key => {
        if (typeof formData[key] === 'boolean') {
          submitData.append(key, formData[key].toString());
        } else if (formData[key]) {
          submitData.append(key, formData[key]);
        }
      });

      // Add areas of specialization
      submitData.append('areasOfSpecialization', JSON.stringify(areasOfSpecialization));

      // Add logo file if uploaded
      if (logoFile) {
        submitData.append('institutionLogo', logoFile);
      }

      const response = await axios.post(
        buildApiUrl('/api/university/complete-registration'),
        submitData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        // Clear saved form data on successful submission
        clearSavedData();
        
        // Update localStorage with profile completion status
        localStorage.setItem('profileCompleted', 'true');
        localStorage.setItem('hasCompletedProfile', 'true');
        
        // Update user context to mark profile as completed
        if (typeof updateUser === 'function') {
          updateUser({ 
            profileCompleted: true,
            hasCompletedProfile: true
          });
        }
        
        // Show success message
        alert('✅ University registration completed successfully! Redirecting to your dashboard...');
        
        // Force refresh of user data to ensure profile completion is detected
        setTimeout(() => {
          // Clear any cached data and reload to ensure fresh authentication state
          sessionStorage.clear();
          localStorage.setItem('profileCompleted', 'true');
          localStorage.setItem('hasCompletedProfile', 'true');
          // Reload the page to ensure fresh authentication state
          window.location.href = '/university-dashboard';
        }, 1000);
      } else {
        setSubmitError(response.data.message || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setSubmitError(error.response?.data?.message || 'Failed to complete registration. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="recruiter-details-comprehensive">
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .recruiter-details-comprehensive {
          max-width: 100% !important;
        }
        .recruiter-header {
          width: 100% !important;
          max-width: 100% !important;
          margin-left: 0 !important;
          margin-right: 0 !important;
          padding-left: 0 !important;
          padding-right: 0 !important;
        }
        .recruiter-header .header-container {
          display: flex !important;
          align-items: center !important;
          justify-content: flex-start !important;
          padding: 12px 20px 12px 10px !important;
          max-width: 100% !important;
          margin: 0 !important;
          width: 100% !important;
        }
        .recruiter-header .header-left {
          display: flex !important;
          flex-direction: row !important;
          align-items: center !important;
          gap: 12px !important;
          margin-right: auto !important;
          margin-left: 0 !important;
          padding-left: 0 !important;
          flex: 0 0 auto !important;
          order: 1 !important;
        }
        .recruiter-header .header-left .logo-icon {
          flex-shrink: 0 !important;
        }
        .recruiter-header .header-left .logo-text {
          flex-shrink: 0 !important;
        }
        .recruiter-header .header-right {
          margin-left: auto !important;
          flex: 0 0 auto !important;
          order: 2 !important;
        }
      `}</style>
      
      {/* Header */}
      <header className="recruiter-header">
        <div className="header-container">
          <div className="header-left" onClick={() => navigate('/university-dashboard')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="logo-icon">
              <img src={logoImage} alt="AksharJobs Logo" style={{ width: '50px', height: '50px', objectFit: 'contain' }} />
            </div>
            <div className="logo-text">
              <h3 className="logo-title" style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>AksharJobs</h3>
              <p className="logo-subtitle" style={{ margin: 0, fontSize: '13px', color: '#6c757d' }}>University Registration</p>
            </div>
          </div>
          <div className="header-right">
            <div className="header-controls" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div className="auto-save-status" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                {isSaving && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#f39c12' }}>
                    <div className="spinner" style={{ width: '12px', height: '12px', border: '2px solid #f39c12', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                    <span>Auto-saving...</span>
                  </div>
                )}
                {saveStatus === 'saved' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#27ae60' }}>
                    <FontAwesomeIcon icon={faCheck} />
                    <span>Auto-saved {lastSaveTime && `at ${lastSaveTime.toLocaleTimeString()}`}</span>
                  </div>
                )}
                {saveStatus === 'error' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#e74c3c' }}>
                    <FontAwesomeIcon icon={faTimes} />
                    <span>Save failed - data preserved locally</span>
                  </div>
                )}
                {saveStatus === 'loaded' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#3498db' }}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <span>Previous data restored</span>
                  </div>
                )}
                {!isSaving && !saveStatus && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#6c757d' }}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <span>Auto-save enabled (every 2 minutes)</span>
                  </div>
                )}
              </div>
              <button
                className="manual-save-btn"
                type="button"
                onClick={handleManualSave}
                disabled={isSaving || isLoading}
                style={{
                  background: (isSaving || isLoading) ? '#bdc3c7' : '#3498db',
                  color: 'white',
                  border: 'none',
                  padding: '8px 15px',
                  borderRadius: '6px',
                  cursor: (isSaving || isLoading) ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  marginRight: '10px',
                  transition: 'all 0.3s ease'
                }}
              >
                <FontAwesomeIcon icon={faSave} /> {(isSaving || isLoading) ? 'Saving...' : 'Save Now'}
              </button>
              <button
                className="draft-save-btn"
                type="button"
                onClick={handleSaveAsDraft}
                disabled={isLoading}
                style={{
                  background: isLoading ? '#bdc3c7' : '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '8px 15px',
                  borderRadius: '6px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  marginRight: '10px',
                  transition: 'all 0.3s ease'
                }}
              >
                <FontAwesomeIcon icon={faEdit} /> Save as Draft
              </button>
              <button
                className="fill-test-data-btn"
                type="button"
                onClick={() => {
                  if (window.confirm('This will fill all form fields with test data. Continue?')) {
                    const testLat = -1.2921;
                    const testLng = 36.8219;
                    
                    // Fill test data
                    setFormData(prev => ({
                      ...prev,
                      universityName: 'University of Nairobi',
                      institutionType: 'Public University',
                      registrationNumber: 'REG/2024/001',
                      cueRegistrationNumber: 'CUE/PC/001',
                      yearEstablished: '1956',
                      institutionLogo: prev.institutionLogo || '',
                      
                      officialEmail: 'info@uonbi.ac.ke',
                      alternativeEmail: 'admin@uonbi.ac.ke',
                      phoneNumber: '+254 20 491 0000',
                      whatsappNumber: '+254 712 345 678',
                      physicalAddress: 'University Way, P.O. Box 30197',
                      city: 'Nairobi',
                      county: 'Nairobi',
                      postalCode: '00100',
                      latitude: testLat.toString(),
                      longitude: testLng.toString(),
                      
                      contactPersonName: 'Dr. Jane Wanjiku',
                      contactPersonTitle: 'Director of Career Services',
                      contactPersonDepartment: 'Career Development Office',
                      contactPersonPhone: '+254 712 345 679',
                      contactPersonEmail: 'placement@uonbi.ac.ke',
                      
                      websiteUrl: 'https://www.uonbi.ac.ke',
                      totalStudentEnrollment: '50000',
                      numberOfPrograms: '150',
                      institutionDescription: 'The University of Nairobi is a world-class institution of higher learning committed to excellence in teaching, research, and innovation. Established in 1956, we have been at the forefront of academic excellence in Kenya and the region.',
                      accreditationBodies: 'CUE, ISO 9001:2015, AAU',
                      
                      careerCenterEmail: 'careers@uonbi.ac.ke',
                      careerOfficePhone: '+254 712 345 680',
                      careerServicesContactPerson: 'Mr. Peter Kamau - Career Services Coordinator',
                      
                      linkedinProfile: 'https://linkedin.com/company/university-of-nairobi',
                      facebookPage: 'https://facebook.com/universityofnairobi',
                      twitterHandle: '@UoNairobi',
                      
                      agreeTerms: false,
                      agreeDataPrivacy: false,
                      verifyAuthorization: false
                    }));
                    
                    // Fill areas of specialization
                    setAreasOfSpecialization([
                      'Engineering',
                      'Business Administration',
                      'Information Technology',
                      'Medicine & Health Sciences',
                      'Education',
                      'Arts & Humanities',
                      'Sciences'
                    ]);
                    
                    // Update map marker if map is loaded
                    setTimeout(() => {
                      if (mapInstanceRef.current) {
                        mapInstanceRef.current.setView([testLat, testLng], 15);
                        setMarker(testLat, testLng, false);
                      }
                    }, 100);
                    
                    alert('Test data filled successfully!');
                  }
                }}
                style={{
                  background: '#f39c12',
                  border: 'none',
                  color: 'white',
                  padding: '8px 15px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  marginRight: '10px',
                  transition: 'all 0.3s ease'
                }}
              >
                Fill Test Data
              </button>
              <button
                className="generate-pdf-btn"
                type="button"
                onClick={generatePDF}
                style={{
                  background: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  padding: '8px 15px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  marginRight: '10px',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <FontAwesomeIcon icon={faFilePdf} /> Generate PDF
              </button>
              <button
                className="clear-form-btn"
                type="button"
                onClick={() => {
                  if (window.confirm('Are you sure you want to clear all saved form data? This cannot be undone.')) {
                    clearSavedData();
                    window.location.reload();
                  }
                }}
                style={{
                  background: 'transparent',
                  border: '1px solid #e74c3c',
                  color: '#e74c3c',
                  padding: '8px 15px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  transition: 'all 0.3s ease'
                }}
              >
                Clear Form Data
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="recruiter-main">
        <div className="recruiter-container">
          <div className="recruiter-form-card">
            {/* Main Form */}
            <div className="recruiter-form-container">
              <form id="universityForm" onSubmit={handleSubmit}>
                {/* Enhanced Progress Section */}
                <div className="progress-section-comprehensive" style={{
                  background: '#ffffff',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '30px',
                  color: '#000000',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  border: '2px solid rgba(0, 0, 0, 0.1)'
                }}>
                  <div className="progress-header-comprehensive" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '15px'
                  }}>
                    <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#000000' }}>
                      <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '10px', color: '#0d9488' }} />
                      Registration Progress
                    </h2>
                    <div style={{
                      background: '#f5f5f5',
                      padding: '8px 16px',
                      borderRadius: '20px',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: '#000000'
                    }}>
                      {Math.round(progress)}%
                    </div>
                  </div>
                  
                  <div className="progress-bar-comprehensive" style={{
                    background: '#f5f5f5',
                    height: '12px',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    marginBottom: '15px',
                    border: '1px solid rgba(0, 0, 0, 0.1)'
                  }}>
                    <div 
                      className="progress-fill-comprehensive" 
                      style={{ 
                        width: `${progress}%`,
                        height: '100%',
                        background: '#0d9488',
                        borderRadius: '6px',
                        transition: 'width 0.5s ease-in-out',
                        position: 'relative'
                      }}
                    >
                      <div style={{
                        position: 'absolute',
                        right: '0',
                        top: '0',
                        height: '100%',
                        width: '20px',
                        background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 100%)',
                        borderRadius: '0 6px 6px 0'
                      }}></div>
                    </div>
                  </div>

                  {/* Progress Status Message */}
                  <div style={{
                    fontSize: '14px',
                    opacity: 0.9,
                    textAlign: 'center',
                    marginBottom: '10px',
                    color: '#000000'
                  }}>
                    {Math.round(progress) === 100 ? (
                      <span style={{ color: '#0d9488', fontWeight: '600' }}>
                        🎉 Profile Complete! Ready to submit.
                      </span>
                    ) : Math.round(progress) >= 80 ? (
                      <span style={{ color: '#000000' }}>
                        Almost there! Just a few more fields to complete.
                      </span>
                    ) : Math.round(progress) >= 50 ? (
                      <span style={{ color: '#000000' }}>
                        Good progress! Keep filling out the form.
                      </span>
                    ) : (
                      <span style={{ color: '#000000' }}>
                        Let's get started! Fill out your institution information.
                      </span>
                    )}
                  </div>

                  {/* Quick Stats */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                    gap: '10px',
                    fontSize: '12px',
                    marginBottom: '15px',
                    color: '#000000'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#000000' }}>8</div>
                      <div style={{ color: '#000000' }}>Sections</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#000000' }}>30+</div>
                      <div style={{ color: '#000000' }}>Fields</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#000000' }}>Auto-save</div>
                      <div style={{ color: '#000000' }}>Enabled</div>
                    </div>
                  </div>

                  {/* Section Completion Checklist */}
                  {Math.round(progress) < 100 && (
                    <div style={{
                      background: '#f5f5f5',
                      borderRadius: '8px',
                      padding: '12px',
                      fontSize: '12px',
                      border: '1px solid rgba(0, 0, 0, 0.1)'
                    }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#000000' }}>
                        Complete these sections:
                      </div>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                        gap: '6px'
                      }}>
                        {[
                          { key: 'institution', name: 'Institution Info', icon: '🏛️', completed: formData.universityName && formData.institutionType && formData.registrationNumber },
                          { key: 'contact', name: 'Contact Info', icon: '📧', completed: formData.officialEmail && formData.phoneNumber && formData.physicalAddress },
                          { key: 'person', name: 'Contact Person', icon: '👤', completed: formData.contactPersonName && formData.contactPersonTitle && formData.contactPersonEmail },
                          { key: 'details', name: 'Institution Details', icon: '📄', completed: formData.websiteUrl || formData.institutionDescription },
                          { key: 'career', name: 'Career Services', icon: '💼', completed: formData.careerCenterEmail || formData.careerOfficePhone },
                          { key: 'specialization', name: 'Specialization', icon: '🎓', completed: areasOfSpecialization.length > 0 },
                          { key: 'social', name: 'Social Media', icon: '🔗', completed: formData.linkedinProfile || formData.facebookPage },
                          { key: 'terms', name: 'Terms', icon: '✅', completed: formData.agreeTerms && formData.agreeDataPrivacy && formData.verifyAuthorization }
                        ].map(section => (
                          <div key={section.key} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            opacity: section.completed ? 0.6 : 1
                          }}>
                            <span style={{
                              color: section.completed ? '#0d9488' : '#000000',
                              fontSize: '14px'
                            }}>
                              {section.completed ? '✅' : section.icon}
                            </span>
                            <span style={{
                              textDecoration: section.completed ? 'line-through' : 'none',
                              opacity: section.completed ? 0.7 : 1,
                              color: '#000000'
                            }}>
                              {section.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {submitError && (
                  <div className="error-message" style={{ marginBottom: '20px', padding: '12px', background: '#fee', border: '1px solid #fcc', borderRadius: '6px', color: '#c33' }}>
                    <FontAwesomeIcon icon={faInfoCircle} /> {submitError}
                  </div>
                )}

                {/* Institution Information Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faUniversity} />
                    Institution Information
                  </h2>

                  {logoPreview && (
                    <div className="profile-photo-preview-comprehensive" style={{ display: 'block' }}>
                      <img src={logoPreview} alt="Institution Logo" />
                    </div>
                  )}

                  <div className="form-group-comprehensive">
                    <label className="file-upload-comprehensive">
                      <FontAwesomeIcon icon={faImage} />
                      <div className="file-upload-text-comprehensive">
                        <strong>Upload Institution Logo</strong>
                        <p>Click to browse or drag and drop (PNG, JPG, max 5MB)</p>
                      </div>
                      <input type="file" accept="image/*" onChange={handleLogoChange} />
                    </label>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>University/Institution Name <span className="required">*</span></label>
                    <input
                      type="text"
                      name="universityName"
                      value={formData.universityName}
                      onChange={handleInputChange}
                      placeholder="University of Nairobi"
                      required
                    />
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Institution Type <span className="required">*</span></label>
                      <select
                        name="institutionType"
                        value={formData.institutionType}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Type</option>
                        {INSTITUTION_TYPES.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Year Established</label>
                      <input
                        type="number"
                        name="yearEstablished"
                        value={formData.yearEstablished}
                        onChange={handleInputChange}
                        placeholder="1956"
                        min="1800"
                        max={new Date().getFullYear()}
                      />
                    </div>
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Registration/Accreditation Number <span className="required">*</span></label>
                      <input
                        type="text"
                        name="registrationNumber"
                        value={formData.registrationNumber}
                        onChange={handleInputChange}
                        placeholder="REG/2024/001"
                        required
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>CUE Registration Number (Kenya)</label>
                      <input
                        type="text"
                        name="cueRegistrationNumber"
                        value={formData.cueRegistrationNumber}
                        onChange={handleInputChange}
                        placeholder="CUE/PC/001"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Information Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faEnvelope} />
                    Contact Information
                  </h2>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Official Email Address <span className="required">*</span></label>
                      <input
                        type="email"
                        name="officialEmail"
                        value={formData.officialEmail}
                        onChange={handleInputChange}
                        placeholder="info@university.ac.ke"
                        required
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Alternative Email</label>
                      <input
                        type="email"
                        name="alternativeEmail"
                        value={formData.alternativeEmail}
                        onChange={handleInputChange}
                        placeholder="admin@university.ac.ke"
                      />
                    </div>
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Phone Number <span className="required">*</span></label>
                      <PhoneInput
                        value={formData.phoneNumber}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, phoneNumber: value }));
                          updateProgress();
                        }}
                        placeholder="+254 712 345 678"
                        required
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>WhatsApp Number</label>
                      <PhoneInput
                        value={formData.whatsappNumber}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, whatsappNumber: value }));
                          updateProgress();
                        }}
                        placeholder="+254 712 345 678"
                      />
                    </div>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Physical Address <span className="required">*</span></label>
                    <input
                      type="text"
                      name="physicalAddress"
                      value={formData.physicalAddress}
                      onChange={handleInputChange}
                      placeholder="University Way, P.O. Box 30197"
                      required
                    />
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>City <span className="required">*</span></label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="Nairobi"
                        required
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>County <span className="required">*</span></label>
                      <select
                        name="county"
                        value={formData.county}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select County</option>
                        {KENYAN_COUNTIES.map(county => (
                          <option key={county} value={county}>{county}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Postal Code</label>
                      <input
                        type="text"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleInputChange}
                        placeholder="00100"
                      />
                    </div>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Pin Your Institution Location on Map (Optional)</label>
                    <div className="info-badge">
                      <FontAwesomeIcon icon={faMapMarkerAlt} /> Click on the map to mark your institution location or search for a place
                    </div>

                    {/* Location Search Box */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '10px', 
                      marginBottom: '10px',
                      marginTop: '10px'
                    }}>
                      <input
                        ref={mapSearchInputRef}
                        type="text"
                        placeholder="Search for a location (e.g., University of Nairobi, Nairobi, Kenya)"
                        onKeyPress={handleSearchKeyPress}
                        style={{
                          flex: 1,
                          padding: '10px 15px',
                          border: '2px solid #e2e8f0',
                          borderRadius: '8px',
                          fontSize: '14px',
                          outline: 'none',
                          transition: 'all 0.3s ease'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#4a90e2';
                          e.target.style.boxShadow = '0 0 0 3px rgba(74, 144, 226, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e2e8f0';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (mapSearchInputRef.current) {
                            searchLocation(mapSearchInputRef.current.value);
                          }
                        }}
                        disabled={isSearching}
                        style={{
                          padding: '10px 20px',
                          background: isSearching ? '#bdc3c7' : '#4a90e2',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: isSearching ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: '600',
                          transition: 'all 0.3s ease',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseOver={(e) => {
                          if (!isSearching) {
                            e.target.style.background = '#357abd';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (!isSearching) {
                            e.target.style.background = '#4a90e2';
                          }
                        }}
                      >
                        {isSearching ? 'Searching...' : 'Search'}
                      </button>
                    </div>

                    <div ref={mapRef} className="map-container" style={{ height: '300px', width: '100%', marginTop: '10px' }}>
                      {!mapLoaded && (
                        <div style={{ 
                          position: 'absolute', 
                          top: '50%', 
                          left: '50%', 
                          transform: 'translate(-50%, -50%)', 
                          color: '#666',
                          fontSize: '14px'
                        }}>
                          Loading map...
                        </div>
                      )}
                    </div>
                    {mapLoaded && (
                      <div className="coordinates-display">
                        <strong>Selected Coordinates:</strong> 
                        {formData.latitude && formData.longitude 
                          ? `Lat: ${parseFloat(formData.latitude).toFixed(6)}, Lng: ${parseFloat(formData.longitude).toFixed(6)}`
                          : ' Click on the map to select'}
                      </div>
                    )}
                  </div>
                </div>

                {/* Primary Contact Person Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faUser} />
                    Primary Contact Person (Placement Officer)
                  </h2>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Full Name <span className="required">*</span></label>
                      <input
                        type="text"
                        name="contactPersonName"
                        value={formData.contactPersonName}
                        onChange={handleInputChange}
                        placeholder="John Doe"
                        required
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Job Title/Position <span className="required">*</span></label>
                      <input
                        type="text"
                        name="contactPersonTitle"
                        value={formData.contactPersonTitle}
                        onChange={handleInputChange}
                        placeholder="Director of Career Services"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Department</label>
                    <input
                      type="text"
                      name="contactPersonDepartment"
                      value={formData.contactPersonDepartment}
                      onChange={handleInputChange}
                      placeholder="Career Development Office"
                    />
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Direct Phone Number <span className="required">*</span></label>
                      <PhoneInput
                        value={formData.contactPersonPhone}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, contactPersonPhone: value }));
                          updateProgress();
                        }}
                        placeholder="+254 712 345 678"
                        required
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Email Address <span className="required">*</span></label>
                      <input
                        type="email"
                        name="contactPersonEmail"
                        value={formData.contactPersonEmail}
                        onChange={handleInputChange}
                        placeholder="placement@university.ac.ke"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Institution Details Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faFileAlt} />
                    Institution Details
                  </h2>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Website URL</label>
                      <input
                        type="url"
                        name="websiteUrl"
                        value={formData.websiteUrl}
                        onChange={handleInputChange}
                        placeholder="https://www.university.ac.ke"
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Total Student Enrollment</label>
                      <input
                        type="number"
                        name="totalStudentEnrollment"
                        value={formData.totalStudentEnrollment}
                        onChange={handleInputChange}
                        placeholder="5000"
                        min="0"
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Number of Programs Offered</label>
                      <input
                        type="number"
                        name="numberOfPrograms"
                        value={formData.numberOfPrograms}
                        onChange={handleInputChange}
                        placeholder="45"
                        min="0"
                      />
                    </div>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Accreditation Bodies</label>
                    <input
                      type="text"
                      name="accreditationBodies"
                      value={formData.accreditationBodies}
                      onChange={handleInputChange}
                      placeholder="CUE, ISO 9001:2015, etc."
                    />
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Brief Description/About Us (3000 characters max)</label>
                    <textarea
                      name="institutionDescription"
                      value={formData.institutionDescription}
                      onChange={handleInputChange}
                      placeholder="Describe your institution, mission, vision, and achievements..."
                      rows="4"
                      maxLength="3000"
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>{formData.institutionDescription.length}/3000 characters</small>
                  </div>
                </div>

                {/* Career Services Information Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faBriefcase} />
                    Career Services/Placement Office
                  </h2>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Career Center Email</label>
                      <input
                        type="email"
                        name="careerCenterEmail"
                        value={formData.careerCenterEmail}
                        onChange={handleInputChange}
                        placeholder="careers@university.ac.ke"
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Career Office Phone</label>
                      <PhoneInput
                        value={formData.careerOfficePhone}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, careerOfficePhone: value }));
                          updateProgress();
                        }}
                        placeholder="+254 712 345 678"
                      />
                    </div>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Career Services Contact Person</label>
                    <input
                      type="text"
                      name="careerServicesContactPerson"
                      value={formData.careerServicesContactPerson}
                      onChange={handleInputChange}
                      placeholder="Name of career services coordinator"
                    />
                  </div>
                </div>

                {/* Areas of Specialization Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faGraduationCap} />
                    Areas of Specialization
                  </h2>

                  <div className="checkbox-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '12px',
                    marginTop: '15px'
                  }}>
                    {AREAS_OF_SPECIALIZATION.map(area => (
                      <label key={area} className="checkbox-label" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}>
                        <input
                          type="checkbox"
                          checked={areasOfSpecialization.includes(area)}
                          onChange={() => handleSpecializationChange(area)}
                          style={{ cursor: 'pointer' }}
                        />
                        <span>{area}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Social Media & Optional Info Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faGlobe} />
                    Social Media & Additional Information
                  </h2>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>
                        <FontAwesomeIcon icon={faLinkedin} style={{ color: '#0077B5', marginRight: '8px' }} />
                        LinkedIn Profile
                      </label>
                      <input
                        type="url"
                        name="linkedinProfile"
                        value={formData.linkedinProfile}
                        onChange={handleInputChange}
                        placeholder="https://linkedin.com/company/your-university"
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>
                        <FontAwesomeIcon icon={faFacebook} style={{ color: '#1877F2', marginRight: '8px' }} />
                        Facebook Page
                      </label>
                      <input
                        type="url"
                        name="facebookPage"
                        value={formData.facebookPage}
                        onChange={handleInputChange}
                        placeholder="https://facebook.com/your-university"
                      />
                    </div>
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>
                        <FontAwesomeIcon icon={faTwitter} style={{ color: '#1DA1F2', marginRight: '8px' }} />
                        Twitter/X Handle
                      </label>
                      <input
                        type="text"
                        name="twitterHandle"
                        value={formData.twitterHandle}
                        onChange={handleInputChange}
                        placeholder="@YourUniversity"
                      />
                    </div>
                    <div></div>
                  </div>
                </div>

                {/* Terms & Agreements Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faShieldAlt} />
                    Terms & Agreements
                  </h2>

                  <div className="checkbox-group" style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '10px',
                    textAlign: 'left',
                    justifyContent: 'flex-start',
                    marginBottom: '15px'
                  }}>
                    <input 
                      type="checkbox" 
                      id="agreeTerms" 
                      name="agreeTerms" 
                      required
                      checked={formData.agreeTerms}
                      onChange={handleInputChange}
                      style={{ 
                        marginTop: '3px',
                        flexShrink: 0,
                        cursor: 'pointer'
                      }}
                    />
                    <label htmlFor="agreeTerms" style={{ 
                      textAlign: 'left',
                      margin: 0,
                      cursor: 'pointer',
                      flex: 1
                    }}>I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms and Conditions</a> <span className="required">*</span></label>
                  </div>

                  <div className="checkbox-group" style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '10px',
                    textAlign: 'left',
                    justifyContent: 'flex-start',
                    marginBottom: '15px'
                  }}>
                    <input 
                      type="checkbox" 
                      id="agreeDataPrivacy" 
                      name="agreeDataPrivacy" 
                      required
                      checked={formData.agreeDataPrivacy}
                      onChange={handleInputChange}
                      style={{ 
                        marginTop: '3px',
                        flexShrink: 0,
                        cursor: 'pointer'
                      }}
                    />
                    <label htmlFor="agreeDataPrivacy" style={{ 
                      textAlign: 'left',
                      margin: 0,
                      cursor: 'pointer',
                      flex: 1
                    }}>I consent to the <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Data Privacy Policy</a> <span className="required">*</span></label>
                  </div>

                  <div className="checkbox-group" style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '10px',
                    textAlign: 'left',
                    justifyContent: 'flex-start',
                    marginBottom: '15px'
                  }}>
                    <input 
                      type="checkbox" 
                      id="verifyAuthorization" 
                      name="verifyAuthorization" 
                      required
                      checked={formData.verifyAuthorization}
                      onChange={handleInputChange}
                      style={{ 
                        marginTop: '3px',
                        flexShrink: 0,
                        cursor: 'pointer'
                      }}
                    />
                    <label htmlFor="verifyAuthorization" style={{ 
                      textAlign: 'left',
                      margin: 0,
                      cursor: 'pointer',
                      flex: 1
                    }}>I verify that I am authorized to register on behalf of this institution <span className="required">*</span></label>
                  </div>

                  {/* Admin Approval Message */}
                  <div style={{
                    marginTop: '20px',
                    padding: '15px',
                    background: '#e3f2fd',
                    border: '1px solid #90caf9',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                    <FontAwesomeIcon 
                      icon={faInfoCircle} 
                      style={{
                        color: '#1976d2',
                        fontSize: '18px',
                        marginTop: '2px',
                        flexShrink: 0
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1976d2',
                        marginBottom: '6px'
                      }}>
                        Account Approval Process
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: '#424242',
                        lineHeight: '1.6'
                      }}>
                        Complete your profile <strong>80%</strong>. If it is <strong>80% completed</strong>, it will be reviewed and then approved by our admin team within <strong>24 hours</strong>. You will receive an email notification once your account is approved. Please check your <strong>spam folder</strong> if you don't see the email in your inbox.
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submit Section */}
                <div className="submit-section" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  {submitError && (
                    <div className="error-message" style={{ marginBottom: '12px', color: '#e74c3c', textAlign: 'center' }}>
                      {submitError}
                    </div>
                  )}
                  <button type="submit" className="submit-btn" disabled={isLoading} style={{ margin: '0 auto' }}>
                    <FontAwesomeIcon icon={faCheckCircle} /> 
                    {isLoading ? 'Submitting...' : 'Complete University Registration'}
                  </button>
                  <p style={{ marginTop: '8px', color: '#666', fontSize: '12px', textAlign: 'center' }}>
                    Your account will be reviewed and activated within 24-48 hours
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UniversityRegistrationForm;

